import { reduce, reduced, replace } from 'ramda'
import moment from 'moment'

import { getPlaceholder, getPrefix } from 'app/common/lib/phone'

import { maskType } from './mask-types'
import { formatWeekdayTime } from './format-weekday-time'
import { formatTime } from './format-time'

const formatByPlaceholder =
  (placeholder: string, prefix: string = '') =>
  (value: string) => {
    const source = value.split('').reverse()
    const placeholderCut = placeholder.substring(prefix.length)
    let index = 0
    const x = reduce(
      (acc: string) => {
        if (source.length === 0) return reduced(acc)
        const res = placeholderCut[index] === '_' ? acc + source.pop() : acc + placeholderCut[index]
        index += 1
        return res
      },
      '',
      placeholderCut.split(''),
    )
    return prefix + x
  }

type PredefinedParams = {
  format: string | ((value: string) => string)
  placeholder: string
  mask: string
  prefix?: string
  removeFormatting?: (v: string) => string
  allowEmptyFormatting?: boolean
}

export const defaultPredefinedParams: PredefinedParams = {
  format: '',
  placeholder: '',
  mask: '',
}

export const getPredefinedParams = (
  type: (typeof maskType)[keyof typeof maskType],
): PredefinedParams => {
  if (type === maskType.MASK_SNILS) {
    const placeholder = '___-___-___ __'
    return {
      format: placeholder.replace(/_/g, '#'),
      placeholder,
      mask: '_',
    }
  }

  if (type === maskType.MASK_DATE) {
    const placeholder = '__.__.____'
    return {
      format: formatByPlaceholder(placeholder),
      placeholder,
      mask: '_',
    }
  }

  if (type === maskType.MASK_DAY_MONTH) {
    const placeholder = '__.__'
    return {
      format: formatByPlaceholder(placeholder),
      placeholder,
      mask: '_',
    }
  }

  if (type === maskType.MASK_TIME) {
    const placeholder = '__ : __'
    return {
      format: placeholder.replace(/_/g, '#'),
      placeholder,
      mask: '_',
    }
  }

  if (type === maskType.MASK_PHONE_RU) {
    const placeholder = getPlaceholder('ru')
    const prefix = getPrefix('ru')
    return {
      format: formatByPlaceholder(placeholder, prefix), // placeholder.replace(/_/g, '#'),
      placeholder,
      prefix,
      removeFormatting: replace(/(^\+7|\D)/g, ''),
      allowEmptyFormatting: true,
      mask: '_',
    }
  }

  if (type === maskType.MASK_PHONE_KG) {
    const placeholder = getPlaceholder('kg')
    const prefix = getPrefix('kg')
    return {
      format: placeholder.replace(/_/g, '#'),
      placeholder,
      prefix,
      removeFormatting: replace(/(^\+996|\D)/g, ''),
      allowEmptyFormatting: true,
      mask: '_',
    }
  }

  if (type === maskType.MASK_PHONE_KZ) {
    const placeholder = getPlaceholder('kz')
    const prefix = getPrefix('kz')
    return {
      format: placeholder.replace(/_/g, '#'),
      placeholder,
      prefix,
      removeFormatting: replace(/(^\+7|\D)/g, ''),
      allowEmptyFormatting: true,
      mask: '_',
    }
  }

  if (type === maskType.MASK_PHONE_UA) {
    const placeholder = getPlaceholder('ua')
    const prefix = getPrefix('ua')
    return {
      format: placeholder.replace(/_/g, '#'),
      placeholder,
      prefix,
      removeFormatting: replace(/(^\+380|\D)/g, ''),
      allowEmptyFormatting: true,
      mask: '_',
    }
  }

  if (type === maskType.MASK_TIME_CUSTOM) {
    const placeholder = '__:__'
    return {
      format: (value: string) => {
        return formatTime(value) || ''
      },
      placeholder,
      mask: '_',
    }
  }

  if (type === maskType.MASK_WEEKDAY_TIME) {
    const placeholder = '________ __:__'
    const locale = moment.locale()
    const minMatchLength = locale === 'bua' ? 'Гарагай 1'.length - 1 : 2

    return {
      format: (value: string) => {
        return (
          formatWeekdayTime(value, {
            localeWeekdays: moment.weekdays(true),
            localeWeekdaysShort: moment.weekdaysShort(true),
            use: 'full',
            minMatchLength,
          }) || ''
        )
      },
      placeholder,
      mask: '_',
    }
  }

  if (type === maskType.MASK_WEEKDAY_SHORT_TIME) {
    const placeholder = '__ __:__'
    const locale = moment.locale()
    const minMatchLength = locale === 'bua' ? 'Гарагай 1'.length - 1 : 2

    return {
      format: (value: string) => {
        return (
          formatWeekdayTime(value, {
            localeWeekdays: moment.weekdays(true),
            localeWeekdaysShort: moment.weekdaysShort(true),
            use: 'short',
            minMatchLength,
          }) || ''
        )
      },
      placeholder,
      mask: '_',
    }
  }

  return defaultPredefinedParams
}
